import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {BoxedIcon} from '../../../../../libs/shared/ui/src/components/icon/boxedIcon';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellPropsWithAvatar,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildCircleColor, CircleModel} from '../../../../models/api/circleModel';

/*
 * Props.
 */

interface CirclesCellProps extends TableCellPropsWithAvatar<ReadonlyArray<CircleModel>> {
  className?: string;
  boxedIcon?: boolean;
  displayDescription?: boolean;
}

/*
 * Style.
 */

interface StyledIconSkeletonProps {
  $avatarSize: number;
}
const StyledIconSkeleton = styled(Skeleton)<StyledIconSkeletonProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  margin-right: 8px;
`;

const StyledCircleCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

const StyledCircleContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  overflow: hidden;
  align-items: center;
  flex: 1;
`;

const StyledContentDiv = styled.div`
  display: grid;
  grid-template-rows: max-content max-content;
  grid-row-gap: 4px;

  &:first-child {
    padding-left: 12px;
  }
`;

const StyledValueDiv = styled.div`
  font-weight: ${fontWeights.semibold};
`;

const StyledDescriptionDiv = styled.div<{$isSelected: boolean}>`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => (p.$isSelected ? p.theme.greys.shade30 : p.theme.greys.shade70)};
`;

const StyledBoxedIconWrapperDiv = styled.div`
  margin: 0 12px;
`;

interface StyledCircleIconProps {
  $shouldAddMargin?: boolean;
}
const StyledCircleIcon = styled(Icon)<StyledCircleIconProps>`
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 999px;

  ${(p) =>
    p.$shouldAddMargin &&
    css`
      margin-right: 8px;
    `}
`;

interface StyledCircleNameDivProps {
  $isSelected: boolean;
}
const StyledCircleNameDiv = styled.div<StyledCircleNameDivProps>`
  line-height: 16px;
  ${ellipsis()};

  ${(p) => getCellTextColor(p.$isSelected)}
`;

/*
 * Component.
 */

export const CirclesCell: FC<CirclesCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    className,
    avatarSize,
    boxedIcon = false,
    displayDescription = false,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const theme = useUserTheme();

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);

  return (
    <TableCell<ReadonlyArray<CircleModel>>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledCircleCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          className={className}
        >
          <StyledCircleContainerDiv>
            <StyledIconSkeleton
              $avatarSize={avatarSizePx}
              height={`${avatarSizePx}px`}
              width={`${avatarSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
            <Skeleton height="16px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledCircleContainerDiv>
        </StyledCircleCellContainerDiv>
      )}
      renderCell={(values) => (
        <StyledCircleCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          className={className}
        >
          {values.map((value) => (
            <StyledCircleContainerDiv key={value.id}>
              {boxedIcon ? (
                <StyledBoxedIconWrapperDiv>
                  <BoxedIcon
                    name="group"
                    size={avatarSize}
                    color={theme.greys.white}
                    backgroundColor={buildCircleColor(theme, value).shade40}
                    borderColor={buildCircleColor(theme, value).shade40}
                  />
                </StyledBoxedIconWrapperDiv>
              ) : (
                <StyledCircleIcon name="group" size={avatarSizePx} $shouldAddMargin />
              )}
              {displayDescription ? (
                <StyledContentDiv>
                  <StyledValueDiv>{value.name}</StyledValueDiv>
                  <StyledDescriptionDiv $isSelected={Boolean(isSelected)}>
                    {value.description}
                  </StyledDescriptionDiv>
                </StyledContentDiv>
              ) : (
                <StyledCircleNameDiv $isSelected={Boolean(isSelected)}>{value.name}</StyledCircleNameDiv>
              )}
            </StyledCircleContainerDiv>
          ))}
        </StyledCircleCellContainerDiv>
      )}
    />
  );
};
